import { Attribute, AttributeItem } from '@/models/Attribute';
import { AttributeOption } from '@/models/AttributeOption';
import { cloneDeep, uniqBy } from 'lodash';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

@Component<AttributeSelector>({})
export default class AttributeSelector extends Vue {
  //#region @Props
  /**
  * ie.
  * @Prop()
  * protected user!: User
  */

  @Prop()
  protected attributes!: Attribute[];
  //#endregion


  //#region @Refs
  /**
  * ie.
  * @Ref()
  * readonly anotherComponent!: AnotherComponent
  */
  //#endregion


  //#region Class properties
  /**
  * ie.
  * protected isLoading = true;
  * * protected company: Company | null = null;
  */

  protected internalAttributes: Attribute[] = [];
  protected selectedAttributesItems: AttributeItem[] = [];
  protected selectedOptions: AttributeOption[] = [];
  //#endregion


  //#region Lifecycle Hooks / Init
  /**
  * ie.
  * protected mounted(): void {
  *  console.log('MyComponent has Mounted');
  * }
  */

  protected async mounted() {
    if (this.attributes) {
      this.parseAttributes();
    } else {
      this.addAttributeItem();
    }
  }
  //#endregion


  //#region Class methods
  /**
   * ie.
   * protected toggleLoading(): void {
   *  this.isLoading = ! this.isLoading;
   * }
   */

  protected selectedOptionsChanged() {
    this.$emit('change', this.selectedOptions);
  }

  protected parseAttributes() {
    // Create AttributeOptions
    this.selectedOptions = this.attributes.map(attribute => {
      return {
        attribute: attribute.id,
        id: attribute.value,
        title: this.getAttributeValue(attribute),
      } as AttributeOption
    });

    // filter uniq Aattribute
    const attributes = uniqBy(this.attributes, (attribute: Record<string, any>) => {
      return attribute.title;
    });

    // parse Attribute to AttributeItem
    this.selectedAttributesItems = attributes.map(attribute => {
      return {
        attribute: attribute
      } as AttributeItem;
    });

    this.selectedOptionsChanged();
  }

  protected getAttributeValue(attribute: Attribute): string | AttributeOption | undefined {
    if (! attribute.options) {
      return attribute.value;
    }

     for (let option of attribute.options) {
       if (option['id'] === attribute.value) {
         return option.title;
       }
     }

     return attribute.value;
  }

  protected addAttributeItem(): void {
    const attributeItem: AttributeItem = {
      attribute: null,
    };

    this.selectedAttributesItems.push(attributeItem);
  }

  protected removeAttributeItem(index: number, attribute: Attribute): void {
    this.selectedAttributesItems.splice(index, 1);

    if (attribute) {
      // Clear all options from selectedOptions
      this.removeAttributeOptions();
    }
  }

  protected removeAttributeOptions() {
    // Map selectedAttributesItems to a string array of id's
    const selectedAttributesItems = this.selectedAttributesItems.map(attributeItem => {
      if (attributeItem.attribute) {
        return attributeItem.attribute.id;
      }
    });

    this.selectedOptions = this.selectedOptions.filter(selectedOption => {
      return selectedAttributesItems.some(attributeId => {
        return attributeId === selectedOption.attribute;
      });
    });

    this.selectedOptionsChanged();
  }

  protected toggleAddAttributeOption(attributeId: string, optionId: string, optionIndex: number): void {
    if (this.isOptionSelected(optionId)) {
      this.selectedOptions.splice(optionIndex, 1);
      return;
    }

    const foundOption = this.findOption(attributeId, optionId);
    if (foundOption) {
      foundOption.attribute = attributeId;
      this.selectedOptions.push(foundOption);
      this.selectedOptionsChanged();
    }
  }

  protected isOptionSelected(optionId: string): boolean {
    return this.selectedOptions.some((currentOption: AttributeOption) => {
      return currentOption.id === optionId;
    });
  }

  protected findOption(attributeId: string, optionId: string): AttributeOption | undefined {
    const foundAttributeItem = this.findAttribute(attributeId);

    if (!foundAttributeItem || !foundAttributeItem.attribute || !foundAttributeItem.attribute.options) {
      return;
    }

    return foundAttributeItem.attribute.options.find(option => {
      return option.id === optionId;
    });
  }

  protected findAttribute(attributeId: string): AttributeItem | undefined {
    return this.selectedAttributesItems.find(attributeItem => {
      return attributeItem.attribute && attributeItem.attribute.id === attributeId;
    });
  }

  protected filteredItems() {
    let attributes: Attribute[] = [];
    this.selectedAttributesItems.forEach((attributeItem) => {
      if (!attributeItem.attribute) {
        return;
      }

      attributes.push(attributeItem.attribute);
    });
    return attributes;
  }
  //#endregion


  //#region Async methods
  /**
  * ie.
  * protected async fetchUserCompany(): Promise<void> {
  *  this.company = await new Company().filter({user: this.user.id}).all();
  * }
  */

  //#endregion


  //#region Getters & Setters
  /**
  * ie.
  * protected get companyName(): string {
  *  return this?.company.name || 'N/A';
  * }
  */

  protected get attributeModel(): Attribute {
    return new Attribute().include('options').limit(300);
  }
  //#endregion


  //#region @Watchers
  /**
  * ie.
  * @Watch('company')
  * protected companyChanged() {
  *  console.log('Company Changed') ;
  * }
  */
  //#endregion
}
